import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { CardUI } from './CardUI';
import { useGameBoard } from './GameBoardContext';
import { useSettings } from './SettingsContext';
import { getHandSum } from '../lib/gameUtils';
import { GAME_CONSTANTS } from '../lib/constants';

export const PlayerArea: React.FC = () => {
  const {
    gameState,
    hoveredCardId,
    dealId,
    revealingCardIds,
    errorMessage,
    showCapturesMenu,
    capturingCards,
    onCardSelect,
    onHoverCard,
    onDeclareLowHand,
    onDeclareThreeAces,
    viewportWidth,
  } = useGameBoard();

  const { playSfx, triggerHaptic } = useSettings();

  const p0 = gameState.players[0];
  const playerScale = viewportWidth < 360 ? 0.8 : viewportWidth < 400 ? 0.9 : 1.0;

  useEffect(() => {
    if (errorMessage) {
      playSfx('error');
      triggerHaptic('warning');
    }
  }, [errorMessage, playSfx, triggerHaptic]);

  return (
    <section className="fixed bottom-0 left-0 right-0 flex flex-col items-center gap-0.5 z-20" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
      {errorMessage && (
        <div className="flex justify-center mb-2">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="glass-panel-rose text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-lg active-glow-panel border border-red-500/30"
          >
            {errorMessage}
          </motion.div>
        </div>
      )}

      <div className="flex justify-between items-end w-full px-2 overflow-visible">
        <div className="w-12 h-20 sm:w-16 sm:h-26 border border-white/20 bg-black/20 rounded-xl flex flex-col items-center justify-center text-white/70 shadow-inner shrink-0">
          <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Deck</span>
          <span className="text-lg sm:text-xl font-serif mt-0.5">{gameState.deck.length}</span>
        </div>

        <div className={cn(
          "flex flex-col items-center px-2 sm:px-4 pt-2 pb-1 rounded-2xl transition-all duration-300",
          gameState.currentPlayerIndex === 0 ? "glass-panel-amber active-glow-panel scale-[1.02]" : "bg-transparent border border-transparent"
        )}>
          {/* Turn indicator for human player */}
          <AnimatePresence>
            {gameState.currentPlayerIndex === 0 && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="mb-1 bg-yellow-500/90 text-black font-black uppercase text-[9px] sm:text-[10px] px-3 py-0.5 rounded-full shadow-lg tracking-wider"
              >
                Il tuo turno
              </motion.div>
            )}
          </AnimatePresence>

          {/* Low Hand / Sbarazzo declare button — only show when player can declare */}
          {(() => {
            const isPlayerTurn = gameState.currentPlayerIndex === 0;
            const notDeclared = !p0.hasDeclaredLowHand;
            const hasThreeCards = p0.hand.length === 3;
            const noAnimation = !gameState.lastPlayedCard;
            const noCapturesMenu = !showCapturesMenu;
            const noCaptureAnim = !capturingCards;
            const sum = getHandSum(p0.hand);
            const lowEnough = sum <= GAME_CONSTANTS.SBARAZZO_DECLARE_THRESHOLD;
            const show = isPlayerTurn && notDeclared && hasThreeCards && noAnimation && noCapturesMenu && noCaptureAnim && lowEnough;
            if (!show && hasThreeCards && lowEnough) {
              console.log('[SBARAZZO-BUTTON] hidden with sum', sum, 'guards:', { isPlayerTurn, notDeclared, hasThreeCards, noAnimation, noCapturesMenu, noCaptureAnim });
            }
            return show ? (
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('medium');
                  onDeclareLowHand();
                }}
                className="mt-1 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-[9px] sm:text-[10px] px-3 py-1 rounded-full shadow-lg tracking-wider transition-colors"
              >
                Dichiara Sbarazzo!
              </button>
            ) : null;
          })()}

          {/* Three Aces declare button — only show when player has 3 aces */}
          {(() => {
            const hasThreeAces = p0.hand.length === 3 && p0.hand.every(c => c.value === 1);
            const canDeclare = gameState.currentPlayerIndex === 0 &&
                                !p0.hasDeclaredThreeAces &&
                                !gameState.lastPlayedCard &&
                                !showCapturesMenu &&
                                !capturingCards;
            if (!canDeclare || !hasThreeAces) return null;
            return (
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('medium');
                  onDeclareThreeAces();
                }}
                className="mt-1 bg-yellow-600 hover:bg-yellow-500 text-white font-black uppercase text-[9px] sm:text-[10px] px-3 py-1 rounded-full shadow-lg tracking-wider transition-colors"
              >
                Dichiara Tre Assi! (+6)
              </button>
            );
          })()}

          <div 
            className="relative z-50 transition-all duration-300" 
            style={{ 
              width: `${Math.round(200 * playerScale)}px`, 
              height: `${Math.round(160 * playerScale)}px`, 
              overflow: 'visible', 
              marginBottom: `${Math.round(-35 * playerScale)}px` 
            }}
          >
            <div 
              className="absolute inset-0 flex items-end justify-center" 
              style={{ paddingBottom: `${Math.round(35 * playerScale)}px` }}
            >
              {p0.hand.map((card, i) => {
                const mid = Math.floor(p0.hand.length / 2);
                const rotation = (i - mid) * 12;
                const yOffset = Math.abs(i - mid) * 6 * playerScale;
                const isExpanded = hoveredCardId === card.id;
                const baseMarginLeft = -28 * playerScale;
                return (
                  <motion.div
                    key={`${dealId}-${card.id}`}
                    layout
                    style={{
                      position: 'relative',
                      marginLeft: i === 0 ? 0 : `${baseMarginLeft}px`,
                      zIndex: isExpanded ? 999 : i,
                      transformOrigin: 'bottom center',
                    }}
                    onClick={() => {
                      if (isExpanded) {
                        playSfx('play');
                        triggerHaptic('light');
                        onCardSelect(card);
                        onHoverCard(null);
                      } else {
                        playSfx('click');
                        triggerHaptic('light');
                        onHoverCard(card.id);
                      }
                    }}
                    animate={{
                      rotate: isExpanded ? 0 : rotation,
                      y: isExpanded ? -60 : -yOffset,
                      scale: isExpanded ? 1.35 : 1,
                      filter: isExpanded ? 'drop-shadow(0 16px 32px rgba(0,0,0,0.7))' : 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                      zIndex: isExpanded ? 999 : i,
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    whileTap={{ scale: 0.96 }}
                  >
                    <CardUI
                      card={card}
                      disabled={gameState.currentPlayerIndex !== 0}
                      isRevealing={revealingCardIds.has(card.id)}
                      isSelected={isExpanded}
                      scale={playerScale}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Deselect backdrop — tap outside cards to collapse */}
          <AnimatePresence>
            {hoveredCardId && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-40"
                onClick={() => onHoverCard(null)}
              />
            )}
          </AnimatePresence>
        </div>

        <div id="prese-pile-0" className="w-12 h-20 sm:w-16 sm:h-26 border border-white/20 bg-black/20 rounded-xl flex flex-col items-center justify-center text-white/70 shadow-inner shrink-0">
          <span className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider">Prese</span>
          <span className="text-lg sm:text-xl font-serif mt-0.5">{p0.captured.length}</span>
        </div>


      </div>
    </section>
  );
};
